/**
 * Lightweight in-memory cache with TTL and key-based invalidation.
 * Deduplicates concurrent requests for the same key so only one HTTP call is made.
 */
interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

export class ApiCache {
  private cache = new Map<string, CacheEntry<unknown>>();
  private pending = new Map<string, Promise<unknown>>();

  /**
   * Get a cached value or invoke the factory, caching the result for `ttlMs`.
   * Concurrent calls with the same key share a single in-flight request.
   */
  async getOrFetch<T>(key: string, ttlMs: number, factory: () => Promise<T>): Promise<T> {
    const cached = this.cache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.value as T;
    }

    // Deduplicate concurrent requests
    const inflight = this.pending.get(key);
    if (inflight) {
      return inflight as Promise<T>;
    }

    const promise = factory()
      .then((value) => {
        this.cache.set(key, { value, expiresAt: Date.now() + ttlMs });
        this.pending.delete(key);
        return value;
      })
      .catch((err) => {
        this.pending.delete(key);
        throw err;
      });

    this.pending.set(key, promise);
    return promise;
  }

  /** Invalidate a single cache key. */
  invalidate(key: string): void {
    this.cache.delete(key);
  }

  /** Invalidate all keys matching a prefix (e.g. 'clients:' clears all client entries). */
  invalidateByPrefix(prefix: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }
  }

  /** Clear the entire cache. */
  clear(): void {
    this.cache.clear();
  }
}
